import PropTypes from 'prop-types';
import StudioGarmentSvg from './StudioGarmentSvg';
import { getArtworkBox, getGarmentLayout, getPrintRect } from './studio2dConfig';
import { getArtworkConfig } from './merchPlaygroundConfig';
import {
  getPreviewArtworkStyle,
  getPreviewLayerStyle,
  getPreviewPrintBox,
  getPreviewPrintBoxStyle,
  getStudioOcclusionMaskUrl,
  getStudioPlateUrl,
  STUDIO_PLATE_SIZE,
} from './studioPreviewConfig';

const MOCKUP_VARIANT_LABELS = {
  flat: 'Flat product mockup',
  hanging: 'Hanging product mockup',
  folded: 'Folded product mockup',
  closeup: 'Collar closeup mockup',
};

const StudioMockupComposer = ({
  productType,
  productColor,
  colorName,
  view,
  artwork,
  artworkLayers,
  artworkTransforms,
  compact = false,
  mockupVariant = 'flat',
  mockupOption,
  usePhotoPlates = false,
}) => {
  const layout = getGarmentLayout(productType);
  const artworkConfig = getArtworkConfig(productType);
  const activeSide = artworkConfig.sides.includes(view) ? view : 'front';
  const artworkEntry = artwork[activeSide];
  const activeLayers = artworkLayers?.[activeSide] || [];
  const transform = artworkTransforms[activeSide] || { offsetX: 0, offsetY: 0, scale: 1 };
  const artworkBox = getArtworkBox(productType, activeSide, transform);
  const variantClass = mockupVariant !== 'flat' ? `is-${mockupVariant}` : '';
  const label = MOCKUP_VARIANT_LABELS[mockupVariant] || MOCKUP_VARIANT_LABELS.flat;
  const previewColorName = colorName || 'White';
  const previewColourClass = previewColorName.toLowerCase().replace(/\s+/g, '-');
  const plateUrl = usePhotoPlates && mockupOption?.plateView
    ? getStudioPlateUrl(productType, mockupOption.plateView, previewColorName)
    : '';
  const hasLiveLayers = activeLayers.length > 0;
  const hasFlattenedLayers = Boolean(artworkEntry?.layers?.length);
  const sourcePrintRect = getPrintRect(productType, activeSide, transform.mode);
  const [previewPrintLeft, previewPrintTop, previewPrintRight, previewPrintBottom] = mockupOption
    ? getPreviewPrintBox(productType, previewColorName, mockupOption, sourcePrintRect, layout)
    : [sourcePrintRect.left, sourcePrintRect.top, sourcePrintRect.left + sourcePrintRect.width, sourcePrintRect.top + sourcePrintRect.height];
  const previewPrintRect = {
    left: previewPrintLeft,
    top: previewPrintTop,
    width: previewPrintRight - previewPrintLeft,
    height: previewPrintBottom - previewPrintTop,
  };
  const photoArtworkStyle = usePhotoPlates && mockupOption && (artworkEntry?.url || hasLiveLayers)
    ? (hasLiveLayers || hasFlattenedLayers
      ? getPreviewPrintBoxStyle(productType, previewColorName, mockupOption, sourcePrintRect, layout)
      : getPreviewArtworkStyle(productType, previewColorName, mockupOption, transform))
    : null;
  const occlusionMaskUrl = usePhotoPlates && mockupOption
    ? getStudioOcclusionMaskUrl(productType, mockupOption.plateView)
    : '';
  const [plateWidth, plateHeight] = STUDIO_PLATE_SIZE;

  if (usePhotoPlates && plateUrl) {
    return (
      <div
        className={`studio-mockup-composer is-photo is-${productType} ${compact ? 'is-compact' : ''} ${variantClass}`}
        data-testid="studio-mockup-composer"
        aria-label={label}
      >
        <div
          className="studio-mockup-frame studio-mockup-frame--photo"
          style={{ aspectRatio: `${plateWidth} / ${plateHeight}` }}
        >
          <div className="studio-mockup-surface studio-mockup-surface--photo">
            <img
              src={plateUrl}
              alt=""
              className="studio-mockup-plate"
              draggable={false}
            />
            {photoArtworkStyle && (
              <div
                className={`studio-mockup-artwork studio-mockup-artwork--photo is-${previewColourClass}`}
                style={photoArtworkStyle}
                data-testid={compact ? undefined : `studio-mockup-artwork-${activeSide}`}
              >
                {hasLiveLayers ? (
                  activeLayers.map((layer, layerIndex) => (
                    <img
                      key={layer.id}
                      src={layer.url}
                      alt=""
                      className="studio-mockup-layer-image"
                      style={getPreviewLayerStyle(layer, activeLayers.length - layerIndex, previewPrintRect)}
                      draggable={false}
                    />
                  ))
                ) : (
                  <img src={artworkEntry.url} alt="" draggable={false} />
                )}
              </div>
            )}
            {photoArtworkStyle && occlusionMaskUrl && (
              <img
                src={plateUrl}
                alt=""
                className="studio-mockup-plate studio-mockup-foreground-occlusion"
                style={{
                  maskImage: `url(${occlusionMaskUrl})`,
                }}
                draggable={false}
              />
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`studio-mockup-composer is-${productType} ${compact ? 'is-compact' : ''} ${variantClass}`}
      data-testid="studio-mockup-composer"
      aria-label={label}
    >
      <div
        className="studio-mockup-frame"
        style={{ width: layout.width, height: layout.height }}
      >
        <div className="studio-mockup-surface">
          <StudioGarmentSvg
            productType={productType}
            view={activeSide}
            fill={productColor}
            className="studio-mockup-garment"
          />
          {artworkEntry?.url && (
            <div
              className="studio-mockup-artwork"
              style={{
                left: artworkBox.x,
                top: artworkBox.y,
                width: artworkBox.width,
                height: artworkBox.height,
              }}
              data-testid={compact ? undefined : `studio-mockup-artwork-${activeSide}`}
            >
              <img src={artworkEntry.url} alt="" draggable={false} />
            </div>
          )}
          <div className="studio-mockup-fabric" aria-hidden="true" />
          <StudioGarmentSvg
            productType={productType}
            view={activeSide}
            fill="transparent"
            className="studio-mockup-linework"
          />
        </div>
      </div>
    </div>
  );
};

StudioMockupComposer.propTypes = {
  productType: PropTypes.string.isRequired,
  productColor: PropTypes.string.isRequired,
  colorName: PropTypes.string,
  view: PropTypes.string.isRequired,
  artwork: PropTypes.object.isRequired,
  artworkLayers: PropTypes.object,
  artworkTransforms: PropTypes.object.isRequired,
  compact: PropTypes.bool,
  mockupVariant: PropTypes.string,
  mockupOption: PropTypes.object,
  usePhotoPlates: PropTypes.bool,
};

export default StudioMockupComposer;
